import React, { useState, useEffect } from 'react';
import ProductOptionsModal from './ProductOptionsModal';
import { useCart } from '../../context/CartContext';

const ProductCard = ({ product, onAddToCart }) => {
    const [showOptions, setShowOptions] = useState(false);
    const [notificationState, setNotificationState] = useState('hidden');
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const { addItem } = useCart();

    const handleAddToCart = () => {
        if (!product.custom_attributes || product.custom_attributes.length === 0) {
            addItem(product);
            setNotificationState('visible');
        } else {
            setShowOptions(true);
        }
    };

    useEffect(() => {
        if (notificationState === 'visible') {
            const timer = setTimeout(() => {
                setNotificationState('exiting');
            }, 1500);
            return () => clearTimeout(timer);
        } else if (notificationState === 'exiting') {
            const exitTimer = setTimeout(() => {
                setNotificationState('hidden');
            }, 300);
            return () => clearTimeout(exitTimer);
        }
    }, [notificationState]);

    const getImageSrc = () => {
        if (product.image_base64) {
            return `data:image/jpeg;base64,${product.image_base64}`;
        }
        if (product.id) {
            return `http://localhost:9000/images/products/${product.id}`;
        }
        return null;
    };

    const imageSrc = getImageSrc();
    const hasImage = imageSrc != null;

    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(false);
    };

    const hasCustomAttributes = product.has_custom_attributes || (product.custom_attributes && product.custom_attributes.length > 0);

    return (
        <>
            <style>
                {`
                    .notification-enter {
                        animation: slideInUp 300ms ease-out forwards;
                    }
                    .notification-exit {
                        animation: slideOutDown 300ms ease-in forwards;
                    }
                    .product-card-premium {
                        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .product-card-premium:hover {
                        transform: translateY(-8px);
                        box-shadow: 0 24px 48px rgba(14, 129, 100, 0.25);
                    }
                    .product-card-premium:hover .card-gradient {
                        opacity: 1;
                    }
                    .add-button-premium {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .add-button-premium:hover {
                        transform: scale(1.05);
                        box-shadow: 0 8px 16px rgba(14, 129, 100, 0.4);
                    }
                    .image-skeleton {
                        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                        background-size: 200% 100%;
                        animation: loading 1.5s infinite;
                    }
                    .card-gradient {
                        transition: opacity 0.4s ease;
                        opacity: 0;
                    }
                    @keyframes loading {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                    @keyframes slideInUp {
                        0% { opacity: 0; transform: translate(-50%, 20px) scale(0.8); }
                        100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
                    }
                    @keyframes slideOutDown {
                        0% { opacity: 1; transform: translate(-50%, 0) scale(1); }
                        100% { opacity: 0; transform: translate(-50%, 20px) scale(0.8); }
                    }
                    @keyframes shimmer {
                        0% { background-position: -200% center; }
                        100% { background-position: 200% center; }
                    }
                    .shimmer-badge {
                        background: linear-gradient(90deg, #0E8164 0%, #10a878 50%, #0E8164 100%);
                        background-size: 200% 100%;
                        animation: shimmer 3s infinite;
                    }
                `}
            </style>

            <div className="relative group">
                <div
                    className="product-card-premium rounded-3xl overflow-hidden relative flex flex-col"
                    style={{
                        background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                        border: '3px solid #0E8164',
                        boxShadow: '0 4px 20px rgba(14, 129, 100, 0.15)',
                        height: '500px',
                        position: 'relative'
                    }}
                >
                    <div
                        className="card-gradient absolute inset-0 rounded-3xl"
                        style={{
                            background: 'linear-gradient(135deg, rgba(14, 129, 100, 0.1) 0%, rgba(14, 129, 100, 0.05) 100%)',
                            pointerEvents: 'none'
                        }}
                    ></div>

                    <div className="relative h-64 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden flex-shrink-0">
                        {!imageLoaded && !imageError && hasImage && (
                            <div className="absolute inset-0 image-skeleton"></div>
                        )}

                        {hasImage && !imageError ? (
                            <img
                                src={imageSrc}
                                alt={product.name}
                                className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                <svg className="w-20 h-20" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                            </div>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>

                        {hasCustomAttributes && (
                            <div className="absolute top-3 left-3">
                                <span className="shimmer-badge text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" />
                                    </svg>
                                    Personalizable
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="relative p-6 flex flex-col flex-1">
                        <h3 className="font-bold text-xl text-gray-900 mb-3 leading-tight" style={{
                            height: '3.5rem',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}>
                            {product.name}
                        </h3>

                        <p className="text-sm text-gray-500 mb-4 leading-relaxed" style={{
                            height: '2.5rem',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}>
                            {product.description || 'Delicioso producto de nuestra cocina'}
                        </p>

                        <div className="mt-auto pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-col">
                                    <span className="text-3xl font-black bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent">
                                        ${product.price.toLocaleString()}
                                    </span>
                                    {hasCustomAttributes && (
                                        <span className="text-xs text-gray-500 font-medium">
                                            desde este precio
                                        </span>
                                    )}
                                </div>

                                <button
                                    onClick={handleAddToCart}
                                    className="add-button-premium px-6 py-3 rounded-xl flex items-center gap-2 text-white font-bold shadow-lg"
                                    style={{
                                        background: 'linear-gradient(135deg, #0E8164 0%, #0a6b52 100%)'
                                    }}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Agregar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {notificationState !== 'hidden' && (
                <div
                    className={`fixed bottom-20 left-1/2 z-50 px-6 py-3 rounded-xl shadow-2xl ${notificationState === 'visible' ? 'notification-enter' : 'notification-exit'}`}
                    style={{
                        background: 'linear-gradient(135deg, #0E8164 0%, #0a6b52 100%)',
                        color: 'white',
                        transform: 'translateX(-50%)'
                    }}
                >
                    <div className="flex items-center space-x-3 text-base font-semibold">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>¡Agregado al carrito!</span>
                    </div>
                </div>
            )}

            {showOptions && (
                <ProductOptionsModal
                    product={product}
                    onClose={() => setShowOptions(false)}
                    onSubmit={(selectedQuantities, totalPrice, attributesDescription, attribute_lines) => {
                        const filteredAttributes = {};
                        Object.entries(selectedQuantities).forEach(([attrId, qty]) => {
                            const attr = product.custom_attributes.find(a => a.id === parseInt(attrId));
                            const defaultQty = attr?.is_default ? attr.default_qty : 0;
                            const diff = qty - defaultQty;
                            if (diff !== 0) {
                                filteredAttributes[attrId] = diff;
                            }
                        });
                        const productOriginalId = String(product.id).split('-custom-')[0];
                        addItem({
                            ...product,
                            id: `${productOriginalId}-custom-${Date.now()}`,
                            original_id: productOriginalId,
                            price: totalPrice,
                            base_price: product.price,
                            customDescription: attributesDescription,
                            selectedAttributes: filteredAttributes,
                            attribute_lines: attribute_lines || [],
                            image_base64: product.image_base64,
                            hasOptions: true
                        });
                        setShowOptions(false);
                        setNotificationState('visible');
                    }}
                />
            )}
        </>
    );
};

export default ProductCard;